import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, ImageOverlay, Marker, Popup, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, MAP_IMAGE } from "@/lib/api";
import { CUSTOM_TYPES } from "@/data/customTypes";
import { Server, Loader2, AlertTriangle } from "lucide-react";

const W = 1000;
const H = 1000;
const BOUNDS = [[0, 0], [H, W]];

function makeIcon(color, letter, pulse) {
  return L.divIcon({
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    html: `<div class="pal-marker ${pulse ? "pulse-ring" : ""}" style="width:30px;height:30px;background:${color}22;border-color:${color};color:${color};box-shadow:0 0 10px ${color}88;">
      <span style="font-size:13px;font-weight:800;">${letter}</span>
    </div>`,
  });
}

export default function SharedMap() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    api.get(`/share/${id}`)
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((e) => setState({ loading: false, error: e.response?.data?.detail || "Lien invalide", data: null }));
  }, [id]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-primary">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center px-6">
        <AlertTriangle className="w-10 h-10 text-destructive" />
        <h1 className="font-head text-2xl font-bold">Carte introuvable</h1>
        <p className="text-muted-foreground text-sm max-w-sm">{state.error}</p>
        <a href="/" className="mt-2 text-primary hover:underline text-sm">Retour à l'accueil</a>
      </div>
    );
  }

  const customMarkers = state.data.markers || [];

  return (
    <div className="relative h-screen w-screen" data-testid="shared-map">
      <MapContainer
        crs={L.CRS.Simple}
        center={[H / 2, W / 2]}
        zoom={-1}
        bounds={BOUNDS}
        maxBounds={BOUNDS}
        maxBoundsViscosity={1}
        minZoom={-1}
        maxZoom={4}
        zoomControl={false}
        attributionControl={false}
        style={{ height: "100%", width: "100%", background: "#0a1a2e" }}
      >
        <ZoomControl position="bottomright" />
        <ImageOverlay url={MAP_IMAGE} bounds={BOUNDS} />

        {customMarkers.map((m) => {
          const meta = CUSTOM_TYPES[m.type] || CUSTOM_TYPES.base;
          const Icon = meta.icon;
          return (
            <Marker key={m.id} position={[H - m.y, m.x]} icon={makeIcon(meta.color, meta.label[0], false)}>
              <Popup>
                <div className="min-w-[190px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    <span className="font-head font-bold text-sm text-white">{m.name}</span>
                  </div>
                  <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: meta.color }}>{meta.label} · perso</div>
                  {m.note && <p className="text-xs text-slate-300 leading-relaxed">{m.note}</p>}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Header banner */}
      <div className="absolute top-4 left-4 z-[500] glass rounded-lg border border-border px-4 py-2.5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
          <Server className="w-4 h-4 text-primary" />
        </div>
        <div className="leading-tight">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-secondary" style={{ color: "#22C55E" }}>Carte partagée · lecture seule</div>
          <div className="font-head font-bold">{state.data.title}</div>
        </div>
        <span className="ml-3 text-xs text-muted-foreground border-l border-border pl-3">{customMarkers.length} marqueur(s)</span>
      </div>
    </div>
  );
}
